import React, { useState, useEffect } from "react";
import DashboardLayout from "../DashboardLayout";
import apiClient from "../../utils/apiClient";
import { 
  Save, 
  X, 
  Edit, 
  Loader2, 
  Search, 
  User, 
  Shield, 
  ShieldCheck, 
  Check, 
  Cpu, 
  Coins, 
  Activity, 
  Users, 
  Lock, 
  Grid,
  Calendar,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from "lucide-react";
import toast from "react-hot-toast";

const categoryIcons = {
  CRM: Users,
  Optimus: Cpu,
  PledgePlus: Coins,
  "Trading Tools": Activity,
  "Dashboard & Core Tools": Grid
};

const permissionCategories = [
  {
    title: "CRM",
    description: "Customer Relationship Management access control",
    color: "text-amber-400 bg-amber-400/10 border-amber-500/20",
    fields: [
      { key: "is_crm_tab", label: "CRM Tab Access" }
    ]
  },
  {
    title: "Optimus",
    description: "Optimus trading system access & options views",
    color: "text-blue-400 bg-blue-400/10 border-blue-500/20",
    fields: [
      { key: "is_optimus_access", label: "Optimus Master Access" },
      { key: "is_optimus_index", label: "Optimus Index Options" },
      { key: "is_optimus_stock", label: "Optimus Stock Options" },
      { key: "is_optimus_commodity", label: "Optimus Commodity Options" },
      { key: "is_optimus_position", label: "Optimus Position View" },
      { key: "is_optimus_orders", label: "Optimus Order View" }
    ]
  },
  {
    title: "PledgePlus",
    description: "Pledge+ margin/collateral management views",
    color: "text-purple-400 bg-purple-400/10 border-purple-500/20",
    fields: [
      { key: "is_pledge_plus_access", label: "Pledge+ Master Access" },
      { key: "is_pledge_plus_positions", label: "Pledge+ Position View" },
      { key: "is_pledge_plus_orders", label: "Pledge+ Orders View" }
    ]
  },
  {
    title: "Trading Tools",
    description: "Active position management, broker raw views, and order monitoring tools",
    color: "text-rose-400 bg-rose-400/10 border-rose-500/20",
    fields: [
      { key: "is_position_manager_tab", label: "Position Manager Tab" },
      { key: "is_broker_raw_position", label: "Broker Raw Position" },
      { key: "is_rejected_orders", label: "Rejected Orders View" },
      { key: "is_exit_position_view", label: "Exit Position View" }
    ]
  },
  {
    title: "Dashboard & Core Tools",
    description: "Primary widgets, analysis tools, and metrics navigation",
    color: "text-emerald-400 bg-emerald-400/10 border-emerald-500/20",
    fields: [
      { key: "is_dashboard_access", label: "Dashboard" },
      { key: "is_live_position_view_access", label: "Live Position View" },
      { key: "is_live_metrics_access", label: "Live Metrics" },
      { key: "is_order_management_access", label: "Order Management" },
      { key: "is_audit_access", label: "Audit" },
      { key: "is_opportunity_screener_access", label: "Opportunity Screener" },
      { key: "is_source_tags_access", label: "Source Tags" },
      { key: "is_portfolio_analyser_access", label: "Portfolio Analyser" },
      { key: "is_bot_config_access", label: "Bot Config" },
      { key: "is_symbol_update", label: "Symbol Update" },
      { key: "is_regime_grid_edit", label: "Regime Grid Edit" },
      { key: "is_alpha_screener_access", label: "Alpha Screener" },
      { key: "is_data_visualizer_access", label: "Data Visualizer" },
      { key: "is_events_calendar_access", label: "Events Calendar" },
      { key: "is_tv_options_chart_access", label: "TV Options Chart" },
      { key: "is_commodity_trades_access", label: "Commodity Trades" },
      { key: "is_ss_greek_access", label: "SS Greek Access" },
      { key: "is_relative_strength_access", label: "Relative Strength" },
      { key: "is_market_breadth_access", label: "Market Breadth" },
      { key: "is_clear_stocks_access", label: "Clear Stocks" }
    ]
  }
];

export default function AdminPanel() {
  const [usersAccess, setUsersAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedUserFilter, setSelectedUserFilter] = useState("all");
  const [expandedLogId, setExpandedLogId] = useState(null);

  useEffect(() => {
    fetchUsersAccess();
  }, []);

  const fetchLogs = async (dateStr) => {
    try {
      setLogsLoading(true);
      const res = await apiClient.get(`admin/action-logs?date=${dateStr}`);
      if (res.data && res.data.status === "success") {
        setLogs(res.data.logs || []);
        setSelectedUserFilter("all");
        setExpandedLogId(null);
      } else {
        toast.error("Failed to fetch activity logs.");
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
      toast.error("Error loading activity logs.");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "activity") {
      fetchLogs(selectedDate);
    }
  }, [activeTab, selectedDate]);

  const userCounts = logs.reduce((acc, log) => {
    const email = log.user_email || "system_or_anonymous";
    acc[email] = (acc[email] || 0) + 1;
    return acc;
  }, {});

  const filteredLogs = logs.filter((log) => {
    if (selectedUserFilter !== "all" && log.user_email !== selectedUserFilter) {
      return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const pathMatch = log.path?.toLowerCase().includes(q);
      const emailMatch = log.user_email?.toLowerCase().includes(q);
      const payloadStr = typeof log.request_payload === "object" 
        ? JSON.stringify(log.request_payload) 
        : String(log.request_payload);
      const payloadMatch = payloadStr?.toLowerCase().includes(q);
      return pathMatch || emailMatch || payloadMatch;
    }
    return true;
  });

  const formatTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (e) {
      return isoString;
    }
  };

  const formatTimeIST = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      });
    } catch (e) {
      return isoString;
    }
  };

  const renderJSON = (data) => {
    if (!data) return <span className="text-gray-600 italic font-medium">None</span>;
    let parsed = data;
    if (typeof data === "string") {
      try {
        parsed = JSON.parse(data);
      } catch (e) {
        return <pre className="text-[11px] text-gray-400 bg-black/40 p-2.5 rounded-xl overflow-x-auto select-text font-mono whitespace-pre-wrap">{data}</pre>;
      }
    }
    return (
      <pre className="text-[11px] text-gray-300 bg-[#0f1116] border border-gray-800/80 p-3 rounded-xl overflow-x-auto select-text font-mono max-h-60 overflow-y-auto custom-scrollbar">
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  };

  const fetchUsersAccess = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("allowed-users");
      if (res.data && res.data.status === "success") {
        setUsersAccess(res.data.users || []);
      } else {
        toast.error("Failed to fetch users access data.");
      }
    } catch (err) {
      console.error("Error fetching users access:", err);
      toast.error("Error fetching users access. Backend endpoint may be missing.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user.email);
    setEditForm({ ...user });
  };

  const handleToggle = (key) => {
    setEditForm((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAllAll = () => {
    setEditForm((prev) => {
      const next = { ...prev };
      permissionCategories.forEach((cat) => {
        cat.fields.forEach((f) => {
          next[f.key] = true;
        });
      });
      return next;
    });
    toast.success("All permissions enabled");
  };

  const handleClearAllAll = () => {
    setEditForm((prev) => {
      const next = { ...prev };
      permissionCategories.forEach((cat) => {
        cat.fields.forEach((f) => {
          next[f.key] = false;
        });
      });
      return next;
    });
    toast.success("All permissions cleared");
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const payload = {
        email: editingUser,
        updates: editForm,
      };
      const res = await apiClient.post("update-user-access", payload);
      if (res.data && res.data.status === "success") {
        toast.success("User access updated successfully.");
        setUsersAccess((prev) =>
          prev.map((u) => (u.email === editingUser ? { ...editForm } : u))
        );
        setEditingUser(null);
      } else {
        toast.error("Failed to update user access.");
      }
    } catch (err) {
      console.error("Error updating user access:", err);
      toast.error("Error updating user access.");
    } finally {
      setIsSaving(false);
    }
  };

  const getAvatarColor = (email) => {
    const colors = [
      "from-pink-500 to-rose-500",
      "from-purple-500 to-indigo-500",
      "from-blue-500 to-cyan-500",
      "from-teal-500 to-emerald-500",
      "from-amber-500 to-orange-500",
    ];
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const getActiveCategories = (user) => {
    const active = [];
    if (user.is_crm_tab) {
      active.push({ name: "CRM", color: "text-amber-400 bg-amber-400/10 border-amber-500/20" });
    }
    if (user.is_optimus_access || user.is_optimus_position || user.is_optimus_orders) {
      active.push({ name: "Optimus", color: "text-blue-400 bg-blue-400/10 border-blue-500/20" });
    }
    if (user.is_pledge_plus_access || user.is_pledge_plus_positions || user.is_pledge_plus_orders) {
      active.push({ name: "Pledge+", color: "text-purple-400 bg-purple-400/10 border-purple-500/20" });
    }
    if (user.is_position_manager_tab || user.is_broker_raw_position || user.is_rejected_orders || user.is_exit_position_view) {
      active.push({ name: "Tools", color: "text-rose-400 bg-rose-400/10 border-rose-500/20" });
    }

    const coreFields = [
      "is_dashboard_access", "is_live_position_view_access", "is_live_metrics_access",
      "is_order_management_access", "is_audit_access", "is_opportunity_screener_access",
      "is_source_tags_access", "is_portfolio_analyser_access", "is_bot_config_access",
      "is_symbol_update", "is_regime_grid_edit", "is_alpha_screener_access", "is_data_visualizer_access",
      "is_events_calendar_access", "is_tv_options_chart_access", "is_commodity_trades_access",
      "is_ss_greek_access", "is_relative_strength_access", "is_clear_stocks_access"
    ];
    const hasCore = coreFields.some(field => user[field] === true);
    if (hasCore) {
      active.push({ name: "Core", color: "text-emerald-400 bg-emerald-400/10 border-emerald-500/20" });
    }
    
    return active;
  };

  // Compute metrics
  const totalUsers = usersAccess.length;
  const adminCount = usersAccess.filter((u) => u.role === "admin").length;
  const standardCount = totalUsers - adminCount;
  
  const totalActivePermissionsCount = usersAccess.reduce((acc, user) => {
    let count = 0;
    permissionCategories.forEach((cat) => {
      cat.fields.forEach((f) => {
        if (user[f.key] === true) {
          count++;
        }
      });
    });
    return acc + count;
  }, 0);

  // Filter lists
  const filteredUsers = usersAccess.filter((user) => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
    if (activeTab === "admin") {
      return matchesSearch && user.role === "admin";
    }
    if (activeTab === "user") {
      return matchesSearch && user.role !== "admin";
    }
    return matchesSearch;
  });

  return (
    <DashboardLayout>
      <div className="flex flex-col flex-1 bg-[#0f1116] text-gray-200 p-6 min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">Admin Control Panel</h1>
          <p className="text-gray-400 text-sm">Manage user access permissions and dashboard modules configuration.</p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[#1a1d29] border border-gray-800/80 rounded-2xl p-6 relative overflow-hidden group hover:border-blue-500/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-indigo-500/0 rounded-full blur-2xl group-hover:scale-150 transition-all duration-500"></div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Total Managed Users</p>
                <h3 className="text-3xl font-extrabold text-white mt-2 tracking-tight">{totalUsers}</h3>
              </div>
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400">
                <Users className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-gray-400 gap-1.5">
              <span className="font-bold text-gray-300">{standardCount}</span> standard users and <span className="font-bold text-gray-300">{adminCount}</span> admin roles configured.
            </div>
          </div>

          <div className="bg-[#1a1d29] border border-gray-800/80 rounded-2xl p-6 relative overflow-hidden group hover:border-purple-500/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-pink-500/0 rounded-full blur-2xl group-hover:scale-150 transition-all duration-500"></div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Admin Role Users</p>
                <h3 className="text-3xl font-extrabold text-white mt-2 tracking-tight">{adminCount}</h3>
              </div>
              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl text-purple-400">
                <ShieldCheck className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs text-gray-400 gap-1.5">
              Full administrator privileges enabled on these accounts.
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          {activeTab === "activity" ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 max-w-2xl">
              {/* Date Selector */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="pl-9 pr-3 py-2 bg-[#1a1d29] border border-gray-800 hover:border-gray-700 focus:border-blue-500 rounded-xl text-xs text-gray-200 outline-none transition-all duration-200"
                />
              </div>
              
              {/* Log Search Filter */}
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Filter logs by path, email, payload..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-[#1a1d29] border border-gray-800 hover:border-gray-700 focus:border-blue-500 rounded-xl text-xs text-gray-200 placeholder-gray-500 outline-none transition-all duration-200"
                />
              </div>
              
              {/* Refresh Button */}
              <button
                onClick={() => fetchLogs(selectedDate)}
                disabled={logsLoading}
                className="p-2 bg-[#1a1d29] hover:bg-gray-800/80 border border-gray-800 focus:outline-none rounded-xl text-gray-400 hover:text-gray-250 transition-all duration-200 disabled:opacity-50 inline-flex items-center justify-center min-w-9"
                title="Refresh Logs"
              >
                <RefreshCw className={`w-4 h-4 ${logsLoading ? "animate-spin" : ""}`} />
              </button>
            </div>
          ) : (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-500" />
              <input
                type="text"
                placeholder="Search user by email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-[#1a1d29] border border-gray-800 hover:border-gray-700 focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/40 rounded-xl text-sm text-gray-200 placeholder-gray-500 outline-none transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-xs font-semibold px-1 py-0.5 rounded bg-gray-800 hover:bg-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5 p-1 bg-[#1a1d29]/80 border border-gray-800/80 rounded-xl self-start md:self-auto">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                activeTab === "all"
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
              }`}
            >
              All Users
            </button>
            <button
              onClick={() => setActiveTab("admin")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                activeTab === "admin"
                  ? "bg-purple-600 text-white shadow-lg shadow-purple-500/20"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
              }`}
            >
              Admins Only
            </button>
            <button
              onClick={() => setActiveTab("user")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                activeTab === "user"
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/20"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
              }`}
            >
              Standard Users
            </button>
            <button
              onClick={() => {
                setActiveTab("activity");
                setSearchQuery("");
              }}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                activeTab === "activity"
                  ? "bg-amber-600 text-white shadow-lg shadow-amber-500/20"
                  : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
              }`}
            >
              Activity Logs
            </button>
          </div>
        </div>

        {activeTab === "activity" ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar Users breakdown */}
            <div className="lg:col-span-1 bg-[#1a1d29] rounded-2xl border border-gray-800/80 p-4 shadow-xl self-start">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4 pb-2 border-b border-gray-800">
                Users Active Today
              </h3>
              {logsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedUserFilter("all")}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs font-bold flex items-center justify-between border transition-all ${
                      selectedUserFilter === "all"
                        ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                        : "bg-[#14161f]/40 border-gray-800 hover:border-gray-700 text-gray-300"
                    }`}
                  >
                    <span>All Users</span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-900 border border-gray-800 text-[10px] text-gray-400 font-bold">
                      {logs.length}
                    </span>
                  </button>
                  
                  {Object.entries(userCounts).map(([email, count]) => (
                    <button
                      key={email}
                      onClick={() => setSelectedUserFilter(email)}
                      className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold flex items-center justify-between border transition-all ${
                        selectedUserFilter === email
                          ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                          : "bg-[#14161f]/40 border-gray-800 hover:border-gray-700 text-gray-300"
                      }`}
                    >
                      <span className="truncate pr-2" title={email}>{email}</span>
                      <span className="px-2 py-0.5 rounded-full bg-gray-900 border border-gray-800 text-[10px] text-gray-400 font-bold">
                        {count}
                      </span>
                    </button>
                  ))}
                  
                  {Object.keys(userCounts).length === 0 && (
                    <div className="text-center py-6 text-xs text-gray-500 italic">
                      No active users on this date
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Detailed Action log feed */}
            <div className="lg:col-span-3 space-y-4">
              {logsLoading ? (
                <div className="bg-[#1a1d29] rounded-2xl border border-gray-800/80 p-12 shadow-xl flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                    <p className="text-sm text-gray-400 font-medium">Loading user activity logs...</p>
                  </div>
                </div>
              ) : (
                <>
                  {filteredLogs.length === 0 ? (
                    <div className="bg-[#1a1d29] rounded-2xl border border-gray-800/80 p-12 shadow-xl text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Activity className="w-10 h-10 text-gray-750 animate-pulse" />
                        <p className="text-base font-semibold text-gray-400">No activity logs found</p>
                        <p className="text-sm text-gray-600">
                          {logs.length > 0
                            ? "Try matching different search terms or user filters."
                            : "There are no recorded state-changing actions for this day."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredLogs.map((log, index) => {
                        const isExpanded = expandedLogId === index;
                        const methodColors = {
                          POST: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
                          PUT: "bg-amber-500/10 text-amber-400 border-amber-500/20",
                          DELETE: "bg-rose-500/10 text-rose-400 border-rose-500/20",
                          PATCH: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20"
                        };
                        const methodColor = methodColors[log.method] || "bg-gray-800 text-gray-400 border-gray-700";
                        const statusColor = log.response_status >= 200 && log.response_status < 300 
                          ? "text-emerald-400 font-bold" 
                          : "text-rose-400 font-bold";

                        return (
                          <div 
                            key={index} 
                            className={`bg-[#1a1d29] rounded-2xl border border-gray-800 hover:border-gray-700/80 shadow-lg overflow-hidden transition-all duration-150 ${
                              isExpanded ? "ring-1 ring-amber-500/30" : ""
                            }`}
                          >
                            {/* Log Summary Header Row */}
                            <div 
                              onClick={() => setExpandedLogId(isExpanded ? null : index)}
                              className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer select-none"
                            >
                              <div className="flex flex-wrap items-center gap-3">
                                <span className="text-xs text-gray-500 font-semibold font-mono">
                                  {formatTime(log.timestamp)}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${methodColor}`}>
                                  {log.method}
                                </span>
                                <span className="text-sm font-bold text-gray-200 tracking-tight font-mono break-all md:break-normal">
                                  {log.path}
                                </span>
                              </div>
                              
                              <div className="flex items-center justify-between md:justify-end gap-4 self-stretch md:self-auto border-t md:border-0 border-gray-800/40 pt-2 md:pt-0">
                                <span className="text-[11px] font-medium text-gray-405 truncate max-w-[180px]" title={log.user_email}>
                                  {log.user_email}
                                </span>
                                <span className={`text-xs ${statusColor} font-mono`}>
                                  HTTP {log.response_status}
                                </span>
                                <span className="text-gray-500">
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4 text-amber-500" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-655" />
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Log Details Panel (Collapsible) */}
                            {isExpanded && (
                              <div className="px-6 pb-6 pt-2 border-t border-gray-800/60 bg-[#14161f]/40 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-3">
                                    <div>
                                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                        Client IP
                                      </span>
                                      <span className="text-xs font-mono text-gray-300 font-semibold">{log.ip_address}</span>
                                    </div>
                                    <div>
                                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                        Timestamp (IST)
                                      </span>
                                      <span className="text-xs font-mono text-gray-300 font-semibold">{formatTimeIST(log.timestamp)}</span>
                                    </div>
                                    <div>
                                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                        Query Parameters
                                      </span>
                                      {Object.keys(log.query_params || {}).length > 0 ? (
                                        renderJSON(log.query_params)
                                      ) : (
                                        <span className="text-xs text-gray-600 italic">None</span>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                      Request Payload
                                    </span>
                                    {log.request_payload ? (
                                      renderJSON(log.request_payload)
                                    ) : (
                                      <span className="text-xs text-gray-600 italic">None</span>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                    Response Body
                                  </span>
                                  {log.response_body ? (
                                    renderJSON(log.response_body)
                                  ) : (
                                    <span className="text-xs text-gray-600 italic">Empty Response</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="bg-[#1a1d29] rounded-2xl border border-gray-800/80 overflow-hidden shadow-xl">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#232736]/60 text-gray-400 text-xs font-bold uppercase tracking-wider border-b border-gray-800">
                      <th className="px-6 py-4 w-16">No.</th>
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Active Modules</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {filteredUsers.map((user, index) => {
                      const activeCats = getActiveCategories(user);
                      return (
                        <tr key={user.email} className="hover:bg-[#202436]/40 transition-colors duration-150">
                          <td className="px-6 py-4 text-sm text-gray-400 font-medium">{index + 1}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white text-sm bg-gradient-to-br ${getAvatarColor(user.email)}`}>
                                {user.email.split("@")[0].substring(0, 2).toUpperCase()}
                              </div>
                              <span className="text-sm text-gray-200 font-semibold">{user.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                              user.role === 'admin' 
                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' 
                                : 'bg-gray-800 text-gray-400 border-gray-700'
                            }`}>
                              {user.role === 'admin' ? (
                                <>
                                  <Shield className="w-3 h-3" />
                                  Admin
                                </>
                              ) : (
                                <>
                                  <User className="w-3 h-3" />
                                  User
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1.5">
                              {activeCats.map((cat, i) => (
                                <span 
                                  key={i} 
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${cat.color}`}
                                >
                                  {cat.name}
                                </span>
                              ))}
                              {activeCats.length === 0 && (
                                <span className="text-xs text-gray-500 italic">No access granted</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleEditClick(user)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 border border-transparent hover:border-blue-500/20 rounded-lg transition-all duration-200"
                              title="Edit Permissions"
                            >
                              <Edit className="w-3.5 h-3.5" />
                              Manage
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <p className="text-base font-semibold">No users found</p>
                            <p className="text-sm text-gray-600">Try adjusting your search query or filters.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        {/* Edit Modal */}
        {editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-[#1a1d29] border border-gray-700/80 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                <h2 className="text-lg font-bold text-white tracking-tight">Edit Permissions</h2>
                <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                {/* Editing Header Box */}
                <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-[#14161f]/70 border border-gray-800/80 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white text-lg bg-gradient-to-br ${getAvatarColor(editingUser)}`}>
                      {editingUser.split("@")[0].substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Editing User Permissions</p>
                      <p className="text-base font-bold text-white mt-0.5">{editingUser}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllAll}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all duration-200"
                    >
                      Select All Permissions
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllAll}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all duration-200"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                <div className="space-y-6">
                  {permissionCategories.map((category) => {
                    const fields = category.fields;
                    const enabledCount = fields.filter((f) => editForm[f.key] === true).length;
                    const totalCount = fields.length;
                    const allEnabled = enabledCount === totalCount;
                    
                    const toggleCategory = () => {
                      setEditForm((prev) => {
                        const next = { ...prev };
                        fields.forEach((f) => {
                          next[f.key] = !allEnabled;
                        });
                        return next;
                      });
                      toast.success(
                        allEnabled 
                          ? `Disabled all ${category.title} permissions` 
                          : `Enabled all ${category.title} permissions`
                      );
                    };

                    const CategoryIcon = categoryIcons[category.title] || Grid;

                    return (
                      <div key={category.title} className="border border-gray-800/80 rounded-xl p-4 bg-[#14161f]/40 relative">
                        {/* Section Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-800/60">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-gray-900 border border-gray-800 text-gray-400">
                              <CategoryIcon className="w-4 h-4" />
                            </div>
                            <div>
                              <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                                {category.title}
                              </h3>
                              <p className="text-[11px] text-gray-400">{category.description}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 self-end sm:self-auto">
                            <span className="text-[11px] px-2.5 py-0.5 rounded-full border bg-gray-800 text-gray-300 border-gray-700 font-semibold">
                              {enabledCount} / {totalCount} Enabled
                            </span>
                            <button
                              type="button"
                              onClick={toggleCategory}
                              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-bold transition-all duration-200 ${
                                allEnabled
                                  ? "bg-rose-500/10 text-rose-400 border-rose-500/30 hover:bg-rose-500/20"
                                  : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20"
                              }`}
                            >
                              {allEnabled ? "Disable Section" : "Enable Section"}
                            </button>
                          </div>
                        </div>
                        
                        {/* Grid items */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {fields.map((field) => (
                            <div 
                              key={field.key} 
                              onClick={() => handleToggle(field.key)}
                              className="flex items-center justify-between p-3 bg-[#0f1116] rounded-xl border border-gray-800/60 hover:border-gray-700/80 cursor-pointer select-none transition-all duration-150"
                            >
                              <span className="text-xs text-gray-300 font-semibold">{field.label}</span>
                              <div
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-all duration-300 focus:outline-none ${
                                  editForm[field.key] 
                                    ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]' 
                                    : 'bg-gray-700'
                                }`}
                              >
                                <span
                                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-all duration-300 ${
                                    editForm[field.key] ? 'translate-x-5' : 'translate-x-1'
                                  }`}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800 bg-[#14161f] rounded-b-2xl">
                <button
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-sm font-semibold text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-extrabold text-black bg-[#07F2C7] rounded-xl hover:bg-[#07F2C7]/90 transition-all duration-200 disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(55, 65, 81, 0.5); border-radius: 9999px; }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(75, 85, 99, 1); }
        `}
      </style>
    </DashboardLayout>
  );
}
